import ERROR_CODES from '@/constants/error-codes'
import ERROR_MESSAGE_TABLE from '@/constants/error-msgs'

const shouldDisplayMessageFromBackend = (code) => {
  return [
    ERROR_CODES.IVR_CONFIRMATION_FAILED,
    ERROR_CODES.IVR_REACHED_MAXIMUM_ATTEMPT,
    ERROR_CODES.NAFATH_REQUEST_WAS_FAILED,
    ERROR_CODES.TAMARA_IS_DISABLE,
    ERROR_CODES.REACHED_CREDIT_LIMIT,
    ERROR_CODES.NOT_PAID_OVERDUE,
    ERROR_CODES.NUMBER_IS_BLACKLIST,
    ERROR_CODES.OUT_OF_SERVICE_AREAS,
    ERROR_CODES.ITEM_VIOLATED,
    ERROR_CODES.SEON_DECLINED_ORDER,
    ERROR_CODES.NAFATH_ID_EXTRACTION_FAILED,
    ERROR_CODES.NAFATH_EXCEED_MAX_ATTEMPT,
    ERROR_CODES.TAHAQAQ_VERIFY_FAILED,
    ERROR_CODES.YAKEEN_VERIFY_FAILED,
    ERROR_CODES.TAHAQAQ_REVERIFY_FAILED,
    ERROR_CODES.KYC_NAFATH_WAS_REJECT,
    ERROR_CODES.YOUTH_REACHED_CREDIT_LIMIT,
    ERROR_CODES.REACHED_CREDIT_LIMIT,
    ERROR_CODES.INVALID_ID,
    ERROR_CODES.KYC_VERIFICATION_UNSUCCESSFUL,
    ERROR_CODES.SUBSCRIPTION_SERVICE_DOWN,
    ERROR_CODES.CARDHOLDER_FAILED_3D_SECURE_AUTHENTICATION,
    ERROR_CODES.INVALID_CARD_NUMBER,
    ERROR_CODES.ISSUER_OR_SWITCH_IS_INOPERATIVE,
    ERROR_CODES.CLOSED_ACCOUNT,
    ERROR_CODES.TRANSACTION_NOT_PERMITTED_TO_CARDHOLDER,
    ERROR_CODES.INVALID_VALUE_AMOUNT,
  ].includes(code)
}

const getErrorTitle = (
  declinedCode,
  paymentType,
  messageFromBackend,
  locale
) => {
  // Use backend error_code to get the equivalent message from luca
  if (shouldDisplayMessageFromBackend(declinedCode) && messageFromBackend) {
    return messageFromBackend
  }

  const keyByDeclinedCode = declinedCode
  const keyByPaymentType = `${declinedCode}_${paymentType}`

  const titleByDeclinedCode = ERROR_MESSAGE_TABLE[keyByDeclinedCode]?.title
  const titleByPaymentType = ERROR_MESSAGE_TABLE[keyByPaymentType]?.title

  // Use backend error_message directly on UI when `locale` is explicitly present in the BE exception response
  if (locale && messageFromBackend) {
    return messageFromBackend
  }

  return (
    titleByDeclinedCode ||
    titleByPaymentType ||
    'instalment_finish_declined_title'
  )
}

const getErrorSubtitle = (
  declinedCode,
  paymentType,
  messageFromBackend,
  locale
) => {
  // Use backend error_code to get the equivalent message from luca
  if (shouldDisplayMessageFromBackend(declinedCode) && messageFromBackend) {
    return messageFromBackend
  }

  const keyByDeclinedCode = declinedCode
  const keyByPaymentType = `${declinedCode}_${paymentType}`

  const subtitleByDeclinedCode =
    ERROR_MESSAGE_TABLE[keyByDeclinedCode]?.subtitle
  const subTitleByPaymentType = ERROR_MESSAGE_TABLE[keyByPaymentType]?.subtitle

  const msg = subtitleByDeclinedCode || subTitleByPaymentType || ''

  if (
    !declinedCode ||
    (TypeChecker.isNumber(declinedCode) && declinedCode < 400)
  ) {
    return ''
  }

  if (msg) {
    return msg
  }

  // Use backend error_message directly on UI when `locale` is explicitly present in the BE exception response
  // in this case, BE is sending back the error message in both English and Arabic based on x-locale header
  if (locale && messageFromBackend) {
    return messageFromBackend
  }

  // Default
  return 'instalment_finish_declined_sub_title'
}

const getErrorTitleForRecoverableFlow = (declinedCode) => {
  const key = ERROR_MESSAGE_TABLE[declinedCode]?.title

  const keyForRecoverbleFlow = key ? `${key}_PIF` : null

  return keyForRecoverbleFlow || 'instalment_finish_declined_title'
}

const getErrorSubtitleForRecoverableFlow = (declinedCode) => {
  const key = ERROR_MESSAGE_TABLE[declinedCode]?.subtitle

  const keyForRecoverbleFlow = key ? `${key}_PIF` : null

  return keyForRecoverbleFlow || 'instalment_finish_declined_sub_title'
}

/**
 * This function pulls out error details from the backend's new error structure or and falls back to the
 * default structure.
 * Expected error structure:
 * {
 *   "error_code": "80104",
 *   "error_message": "Your card is about to expire. Please use another card.",
 *   "locale": "en_US"
 * }
 * Otherwise, it falls back to the default structure.
 */
const handleNewBackendError = (error) => {
  const errorCode =
    (Array.isArray(error?.response?.data) &&
      error.response.data[0]?.error_code) ||
    error?.error_code ||
    error?.response?.data?.error_code ||
    null

  const errorMessage =
    (Array.isArray(error?.response?.data) &&
      error.response.data[0]?.error_message) ||
    error?.error_message ||
    error?.response?.data?.error_message ||
    'An unknown error occurred'

  const locale = error?.locale || error?.response?.data?.locale || null

  return { errorCode, errorMessage, locale }
}

export {
  getErrorTitle,
  getErrorSubtitle,
  getErrorTitleForRecoverableFlow,
  getErrorSubtitleForRecoverableFlow,
  handleNewBackendError,
}
